import React, { useEffect, useState } from 'react';

import { groupTrussDesignDrawingTemplates, TrussDesignDrawingTemplates } from '@paragon/client-tools';
import { TrussDesignDrawingOptions } from '@paragon/contract-library';
import { designService, sealingService } from '@paragon/data-connections';
import { getLoggedInUser, hasFreeSubscription, isLoggedInUserRoot } from '@paragon/login-tools';
import {
  getTrussDesignDrawingOptionsToSend,
  TrussDesignDrawingOptionsFields,
  useTrussDesignDrawingOptions,
} from '@paragon/material';

import { useAppSelector } from '../../hooks';
import { DownloadListItem, DownloadListItemProps } from './DownloadListItem';
import { DownloadMode } from './DownloadsDialog';

const NO_TEMPLATES: TrussDesignDrawingTemplates = {
  trussDesignDrawingTemplates: null,
  trussDesignDrawingOverflowTemplates: null,
  repairTrussDesignDrawingTemplates: null,
};

/**
 * Anonymous users cannot choose templates or leave off the analysis warning text, and free users always get the
 * system templates, so nothing is fetched for them.
 */
export function useTrussDesignDrawingDownloadChoices() {
  const isLoggedIn = !!useAppSelector(getLoggedInUser);
  const isFreeUser = useAppSelector(hasFreeSubscription);

  const [templates, setTemplates] = useState(NO_TEMPLATES);
  const [canIgnoreErrors, setCanIgnoreErrors] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    designService.settings
      .getOrganizationCapabilities()
      .then((capabilities) => {
        if (!cancelled) setCanIgnoreErrors(capabilities.maySuppressAnalysisWarningsOnDrawings);
      })
      .catch(console.error);
    if (!isFreeUser) {
      sealingService.annotationTemplates
        .fetchAllForUserWithoutContents()
        .then((allTemplates) => {
          if (!cancelled) setTemplates(groupTrussDesignDrawingTemplates(allTemplates));
        })
        .catch(console.error);
    }
    return () => void (cancelled = true);
  }, [isLoggedIn, isFreeUser]);

  return { templates, canIgnoreErrors };
}

type Props = Omit<DownloadListItemProps, 'onClick' | 'children'> & {
  readonly mode: DownloadMode;
  readonly templates: TrussDesignDrawingTemplates;
  readonly canIgnoreErrors: boolean;
  readonly canShareLayout: boolean;
  readonly canChooseFileType: boolean;
  readonly onClick: (options: TrussDesignDrawingOptions) => Promise<void>;
};

/**
 * Offers the same options as the Projects client's Truss Design Drawing download dialog. Remount it when `mode`
 * changes, since the defaults depend on it.
 */
export function TrussDesignDrawingDownloadListItem({
  mode,
  templates,
  canIgnoreErrors,
  canShareLayout,
  canChooseFileType,
  onClick,
  ...downloadListItemProps
}: Props) {
  const isRootUser = useAppSelector(isLoggedInUserRoot);

  const [options, setOptions] = useTrussDesignDrawingOptions(templates, {
    mergeIntoOnePdf: true,
    shareLayout: mode !== 'single',
  });

  return (
    <DownloadListItem {...downloadListItemProps} onClick={() => onClick(getTrussDesignDrawingOptionsToSend(options))}>
      <TrussDesignDrawingOptionsFields
        options={options}
        onChange={setOptions}
        templates={templates}
        showFileType={canChooseFileType}
        showForceDarkMode={isRootUser}
        showIgnoreErrors={canIgnoreErrors}
        showShareLayout={canShareLayout}
      />
    </DownloadListItem>
  );
}
